import { useState } from "react";
import { Star } from "lucide-react";
import { ratingBandLabel } from "../hooks/useAppraisalStages";

/**
 * The rating control for an appraisal question.
 *
 * Stars are used only where they can be read honestly: a scale that starts at
 * 1 and is short enough to count at a glance. On anything else -- a scale
 * starting at 0, or one with more points than a person can count -- the nth
 * star would not be the nth rating, so the control falls back to a segmented
 * row of the actual numbers rather than quietly misstating the value.
 *
 * Either way the number and its band are printed beside the control, so the
 * rating never depends on counting shapes or on colour alone.
 */

const STAR_LIMIT = 7;

export function usesStars(min: number, max: number): boolean {
  return min === 1 && max <= STAR_LIMIT && max > min;
}

interface Props {
  value: string;
  min: number;
  max: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Names the group for screen readers -- usually the question text. */
  label: string;
}

/** Shared read-out: "4 of 5 · Exceeds expectations", or the empty prompt. */
function Readout({
  value,
  min,
  max,
  preview,
}: {
  value: string;
  min: number;
  max: number;
  preview: number | null;
}) {
  const shown = preview ?? (value === "" ? null : Number(value));
  if (shown === null) {
    return <span className="text-sm text-secondary-400">Not rated yet</span>;
  }
  return (
    <span className="text-sm">
      <span className="font-semibold text-secondary-900">
        {shown} <span className="font-normal text-secondary-400">of {max}</span>
      </span>
      <span className="mx-1.5 text-secondary-300">·</span>
      <span className={preview !== null ? "text-secondary-500" : "text-secondary-700"}>
        {ratingBandLabel(shown, min, max)}
      </span>
    </span>
  );
}

export default function RatingInput({
  value,
  min,
  max,
  onChange,
  disabled = false,
  label,
}: Props) {
  const [preview, setPreview] = useState<number | null>(null);
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const selected = value === "" ? null : Number(value);
  const stars = usesStars(min, max);

  // Arrow keys walk the scale; Home/End jump to its ends. Without this the
  // control would be mouse-only, which a required field must never be.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    const current = selected ?? min - 1;
    let next: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      next = Math.min(max, current + 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      next = Math.max(min, current - 1);
    } else if (event.key === "Home") {
      next = min;
    } else if (event.key === "End") {
      next = max;
    }
    if (next === null) return;
    event.preventDefault();
    onChange(String(next));
  };

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2"
      onMouseLeave={() => setPreview(null)}
    >
      <div
        role="radiogroup"
        aria-label={label}
        onKeyDown={onKeyDown}
        className={stars ? "flex items-center gap-0.5" : "flex flex-wrap items-center gap-1.5"}
      >
        {points.map((point) => {
          const isSelected = selected === point;
          const lit = stars && point <= (preview ?? selected ?? 0);
          const common = {
            type: "button" as const,
            role: "radio",
            "aria-checked": isSelected,
            "aria-label": `${point} of ${max} — ${ratingBandLabel(point, min, max)}`,
            // Only the selected option is tabbable, so the group is one stop
            // in the tab order and the arrow keys do the rest.
            tabIndex: isSelected || (selected === null && point === min) ? 0 : -1,
            disabled,
            onMouseEnter: () => setPreview(point),
            onFocus: () => setPreview(point),
            onBlur: () => setPreview(null),
            onClick: () => onChange(isSelected ? "" : String(point)),
          };

          return stars ? (
            <button
              key={point}
              {...common}
              className="rounded-md p-1 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed enabled:hover:scale-110"
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  lit ? "fill-gold-400 text-gold-500" : "fill-transparent text-secondary-300"
                }`}
                aria-hidden
              />
            </button>
          ) : (
            <button
              key={point}
              {...common}
              className={`min-w-10 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed ${
                isSelected
                  ? "border-navy-700 bg-navy-700 text-white"
                  : "border-secondary-300 bg-white text-secondary-700 enabled:hover:border-navy-300 enabled:hover:bg-navy-50"
              }`}
            >
              {point}
            </button>
          );
        })}
      </div>

      <Readout value={value} min={min} max={max} preview={preview} />

      {selected !== null && !disabled && (
        <button
          type="button"
          className="text-xs font-medium text-secondary-500 underline-offset-2 hover:text-secondary-800 hover:underline"
          onClick={() => onChange("")}
        >
          Clear
        </button>
      )}
    </div>
  );
}

/** The same rating, rendered as a fact rather than a control. */
export function RatingDisplay({
  value,
  min,
  max,
}: {
  value: number;
  min: number;
  max: number;
}) {
  const stars = usesStars(min, max);
  return (
    <span className="inline-flex items-center gap-2">
      {stars && (
        <span className="flex items-center gap-0.5" aria-hidden>
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((point) => (
            <Star
              key={point}
              className={`h-4 w-4 ${
                point <= value
                  ? "fill-gold-400 text-gold-500"
                  : "fill-transparent text-secondary-300"
              }`}
            />
          ))}
        </span>
      )}
      <span className="text-sm text-secondary-800">
        <span className="font-semibold">{value}</span>
        <span className="text-secondary-400">/{max}</span>
        <span className="mx-1.5 text-secondary-300">·</span>
        {ratingBandLabel(value, min, max)}
      </span>
    </span>
  );
}
